import { api } from 'src/api';
import { createAsync, createSilentAsync } from 'src/utils/asyncModel';
import { types } from 'mobx-state-tree';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
const getCompaniesListAsync = createAsync(body => {
  return async function getCompaniesList(options) {
    const resp = await api.AdminApi.getCompaniesList(body, options);
    return resp;
  };
});
const createPMAccountAsync = createAsync(body => {
  return async function createPMAccount(options) {
    const resp = await api.AdminApi.createPMAccount(body, options);
    return resp;
  };
});
const createCompanyAsync = createAsync(body => {
  return async function createCompany(options) {
    const resp = await api.AdminApi.createCompany(body, options);
    return resp;
  };
});
const getCompanyByIdAsync = createAsync(companyId => {
  return async function getCompanyById(options) {
    const resp = api.AdminApi.getCompanyById({
      companyId
    }, options);
    return resp;
  };
});
const getAdminAccountLoginImpersonateAsync = createAsync(body => {
  return async function getAdminAccountLoginImpersonate(options) {
    const resp = await api.AdminApi.getAdminAccountImpersonate(body, options);
    return resp;
  };
});
const createAdminAccountAsync = createAsync(body => {
  return async function createAdminAccount(options) {
    const resp = await api.AdminApi.createAdminAccount(body, options);
    return resp;
  };
});
const getAdminAccountsAsync = createAsync(() => {
  return async function getAdminAccounts(options) {
    const resp = await api.AdminApi.getAdminAccounts(options);
    return resp;
  };
});
const deleteAdminAccountAsync = createAsync(body => {
  return async function deleteAdminAccount(options) {
    const resp = await api.AdminApi.deleteAdminAccount(body, options);
    return resp;
  };
});
const blockAdminAccountAsync = createAsync(body => {
  return async function blockAdminAccount(options) {
    const resp = await api.AdminApi.blockAdminAccount(body, options);
    return resp;
  };
});
const unblockAdminAccountAsync = createAsync(body => {
  return async function unblockAdminAccount(options) {
    const resp = await api.AdminApi.unblockAdminAccount(body, options);
    return resp;
  };
});
const updateAdminAccountAsync = createAsync(body => {
  return async function updateAdminAccount(options) {
    const resp = await api.AdminApi.updateAdminAccount(body, options);
    return resp;
  };
});

// Silent: both lookups are additive decoration on pages whose primary job is something else, and
// every caller already renders an "unavailable" fallback. A global toast here blocks a page the
// user can still work on, and stays on screen across route changes because it does not auto-close.
const getUtilityCredentialsAsync = createSilentAsync(companyId => {
  return async function getUtilityCredentials(options) {
    return api.AdminApi.getUtilityCredentials(companyId, options);
  };
});
const saveUtilityCredentialAsync = createAsync(body => {
  return async function saveUtilityCredential(options) {
    return api.AdminApi.saveUtilityCredential(body, options);
  };
});
const getBillScrapingHealthAsync = createAsync(function () {
  let days = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 30;
  return async function getBillScrapingHealth(options) {
    return api.AdminApi.getBillScrapingHealth(days, options);
  };
});
const disableUtilityCredentialAsync = createAsync(credentialId => {
  return async function disableUtilityCredential(options) {
    return api.AdminApi.disableUtilityCredential(credentialId, options);
  };
});
const resumeUtilityCredentialAsync = createAsync(credentialId => {
  return async function resumeUtilityCredential(options) {
    return api.AdminApi.resumeUtilityCredential(credentialId, options);
  };
});
const getUtilityCredentialEntitlementAsync = createSilentAsync(companyId => {
  return async function getUtilityCredentialEntitlement(options) {
    return api.AdminApi.getUtilityCredentialEntitlement(companyId, options);
  };
});
const getBillScrapingCoverageAsync = createAsync(() => {
  return async function getBillScrapingCoverage(options) {
    return api.AdminApi.getBillScrapingCoverage(options);
  };
});

// Silent: the console renders this failure on its own card (a 503 where the environment cannot read the scoreboard).
const getBillScrapingScoreboardAsync = createSilentAsync(function () {
  let days = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 90;
  return async function getBillScrapingScoreboard(options) {
    return api.AdminApi.getBillScrapingScoreboard(days, options);
  };
});
const getBillScrapingProvidersAsync = createAsync(() => {
  return async function getBillScrapingProviders(options) {
    return api.AdminApi.getBillScrapingProviders(options);
  };
});
const getBillQueueAsync = createAsync(query => {
  return async function getBillQueue(options) {
    return api.AdminApi.getBillQueue(query, options);
  };
});
const getBillQueueCyclesAsync = createAsync(() => {
  return async function getBillQueueCycles(options) {
    return api.AdminApi.getBillQueueCycles(options);
  };
});
const getBillQueueItemAsync = createAsync(allocationTableId => {
  return async function getBillQueueItem(options) {
    return api.AdminApi.getBillQueueItem(allocationTableId, options);
  };
});
const getBillQueueChangesAsync = createAsync(days => {
  return async function getBillQueueChanges(options) {
    return api.AdminApi.getBillQueueChanges(days, options);
  };
});

// The banner's own model: run() cancels the request in flight, and the Changes tab reads the same endpoint.
const getBillQueueChangesSplitAsync = createAsync(days => {
  return async function getBillQueueChangesSplit(options) {
    return api.AdminApi.getBillQueueChanges(days, options);
  };
});
const markBillQueueChangesSeenAsync = createAsync(through => {
  return async function markBillQueueChangesSeen(options) {
    return api.AdminApi.markBillQueueChangesSeen(through, options);
  };
});
const claimBillQueueItemAsync = createAsync(body => {
  return async function claimBillQueueItem(options) {
    return api.AdminApi.claimBillQueueItem(body, options);
  };
});
const releaseBillQueueItemAsync = createAsync(allocationTableId => {
  return async function releaseBillQueueItem(options) {
    return api.AdminApi.releaseBillQueueItem(allocationTableId, options);
  };
});
const setBillQueueStateAsync = createAsync(body => {
  return async function setBillQueueState(options) {
    return api.AdminApi.setBillQueueState(body, options);
  };
});
const enterBillQueueBillAsync = createAsync(body => {
  return async function enterBillQueueBill(options) {
    return api.AdminApi.enterBillQueueBill(body, options);
  };
});
const revealBillQueueCredentialAsync = createAsync(credentialId => {
  return async function revealBillQueueCredential(options) {
    return api.AdminApi.revealBillQueueCredential(credentialId, options);
  };
});
const adminSettings = types.model({
  companyId: types.optional(types.string, ''),
  companyName: types.optional(types.string, ''),
  isAdminSwitchToNewAccount: types.optional(types.boolean, false)
});
const AdminStore = types.model('AdminStore', {
  settings: types.optional(adminSettings, {}),
  getCompaniesListAsync,
  createPMAccountAsync,
  createCompanyAsync,
  getCompanyByIdAsync,
  createAdminAccountAsync,
  getAdminAccountLoginImpersonateAsync,
  getAdminAccountsAsync,
  deleteAdminAccountAsync,
  updateAdminAccountAsync,
  blockAdminAccountAsync,
  unblockAdminAccountAsync,
  getUtilityCredentialsAsync,
  saveUtilityCredentialAsync,
  disableUtilityCredentialAsync,
  resumeUtilityCredentialAsync,
  getBillScrapingHealthAsync,
  getUtilityCredentialEntitlementAsync,
  getBillScrapingCoverageAsync,
  getBillScrapingProvidersAsync,
  getBillScrapingScoreboardAsync,
  getBillQueueAsync,
  getBillQueueCyclesAsync,
  getBillQueueItemAsync,
  getBillQueueChangesAsync,
  getBillQueueChangesSplitAsync,
  markBillQueueChangesSeenAsync,
  claimBillQueueItemAsync,
  releaseBillQueueItemAsync,
  setBillQueueStateAsync,
  enterBillQueueBillAsync,
  revealBillQueueCredentialAsync
}).actions(store => {
  return {
    saveSelectedCompanyName(name) {
      try {
        store.settings.companyName = name !== null && name !== void 0 ? name : 'Select account';
        localStorage.setItem(USER_SETTINGS_KEYS.companyName, name || 'Select account');
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setIsAdminSwitchToNewAccount(state) {
      try {
        store.settings.isAdminSwitchToNewAccount = state;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    }
  };
}).views(store => ({
  get companyName() {
    if (!store.settings.companyName) {
      store.saveSelectedCompanyName(localStorage.getItem(USER_SETTINGS_KEYS.companyName));
    }
    return localStorage.getItem(USER_SETTINGS_KEYS.companyName) || 'Select account';
  },
  get companyId() {
    return localStorage.getItem(USER_SETTINGS_KEYS.company);
  },
  get isAdminSwitchToNewAccount() {
    return store.settings.isAdminSwitchToNewAccount;
  }
}));
export const admin = types.optional(AdminStore, {});